import { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
// import { useDate } from "../../hooks/useDate";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({})
 

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        const { data } = await api.post("/auth/refresh_token");
        if (data) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(originalRequest);
      }
      if (
        error?.response?.status === 402 &&
        error?.response?.data?.error === "PAYMENT_REQUIRED"
      ) {
        if (window.location.pathname !== "/financeiro") {
          toast.warn(
            "Sua assinatura está vencida. Regularize o pagamento para continuar utilizando a Korax."
          );

          window.location.replace("/financeiro");
        }

        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
      }

      return Promise.reject(error);
    }
  );

  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      try {
        const { data } = await api.post("/auth/refresh_token");

        if (!mounted) return;

        if (!data?.token || !data?.user) {
          throw new Error("Sessão inválida.");
        }

        localStorage.setItem(
          "token",
          JSON.stringify(data.token)
        );

        api.defaults.headers.Authorization =
          `Bearer ${data.token}`;

        setUser(data.user);
        setIsAuth(true);
      } catch (err) {
        if (!mounted) return;

        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setUser({});
        setIsAuth(false);

        if (err?.response?.status !== 401) {
          console.error(
            "[Auth] Não foi possível restaurar a sessão:",
            err
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      // console.log("Entrou useWhatsapp com user", Object.keys(user).length, Object.keys(socket).length ,user, socket)
      let io;
      if (!Object.keys(socket).length) {
        io = socketConnection({ user });
        setSocket(io)
      } else {
        io = socket
      }
      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        // console.log("desconectou o company user ", user.id)
        io.off(`company-${user.companyId}-user`);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);


  const refreshCurrentUser = async () => {
    try {
      const { data } = await api.post("/auth/refresh_token");

      if (data?.token) {
        localStorage.setItem(
          "token",
          JSON.stringify(data.token)
        );

        api.defaults.headers.Authorization =
          `Bearer ${data.token}`;
      }

      if (data?.user) {
        setUser(data.user);
        setIsAuth(true);

        const dueDate =
          data.user?.company?.dueDate;

        if (dueDate) {
          localStorage.setItem(
            "companyDueDate",
            moment(dueDate).format("DD/MM/yyyy")
          );
        }

        return data.user;
      }

      return null;
    } catch (error) {
      console.error(
        "[Auth] Erro ao atualizar usuário atual:",
        error
      );

      throw error;
    }
  };

  const initializeAuthenticatedSession = async (
    authData,
    options = {}
  ) => {
    const {
      targetPath,
      showSuccessToast = true
    } = options;

    setLoading(true);

    try {
      if (
        !authData?.token ||
        !authData?.user ||
        !authData?.user?.company
      ) {
        throw new Error(
          "Dados de autenticação incompletos."
        );
      }

      const authenticatedUser = authData.user;
      const company = authenticatedUser.company;

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings?.[0])
      ) {
        const campaignsSetting =
          company.companieSettings[0].find(
            setting =>
              setting.key === "campaignsEnabled"
          );

        if (
          campaignsSetting &&
          campaignsSetting.value === "true"
        ) {
          localStorage.setItem("cshow", "true");
        }

        const signatureSetting =
          company.companieSettings[0].find(
            setting =>
              setting.key === "sendSignMessage"
          );

        if (
          signatureSetting &&
          signatureSetting.value === "enabled"
        ) {
          localStorage.setItem(
            "sendSignMessage",
            "true"
          );
        }
      }

      if (authenticatedUser.profileImage) {
        localStorage.setItem(
          "profileImage",
          authenticatedUser.profileImage
        );
      }

      moment.locale("pt-br");

      const dueDate =
        company.id === 1
          ? "2999-12-31T00:00:00.000Z"
          : company.dueDate;

      const formattedDueDate = dueDate
        ? moment(dueDate).format("DD/MM/YYYY")
        : "";

      localStorage.setItem(
        "token",
        JSON.stringify(authData.token)
      );

      if (formattedDueDate) {
        localStorage.setItem(
          "companyDueDate",
          formattedDueDate
        );
      }

      api.defaults.headers.Authorization =
        `Bearer ${authData.token}`;

      setUser(authenticatedUser);
      setIsAuth(true);

      const subscriptionIsValid =
        !dueDate ||
        moment().isBefore(dueDate) ||
        moment().isSame(dueDate, "day");

      if (!subscriptionIsValid) {
        if (showSuccessToast) {
          toast.warn(
            `Sua assinatura venceu em ${formattedDueDate}. Escolha um plano para continuar utilizando a Korax.`
          );
        }

        history.replace("/financeiro");
        return authenticatedUser;
      }

      if (showSuccessToast) {
        toast.success(
          i18n.t("auth.toasts.success")
        );

        const remainingDays = dueDate
          ? Math.ceil(
              moment.duration(
                moment(dueDate).diff(moment())
              ).asDays()
            )
          : null;

        if (
          remainingDays !== null &&
          remainingDays >= 0 &&
          remainingDays < 5
        ) {
          toast.warn(
            `Sua assinatura vence em ${remainingDays} ${
              remainingDays === 1 ? "dia" : "dias"
            }`
          );
        }
      }

      const onboardingCompleted =
        Boolean(
          authenticatedUser.company
            ?.onboardingCompletedAt
        );

      history.replace(
        targetPath ||
          (
            onboardingCompleted
              ? "/tickets"
              : "/onboarding"
          )
      );

      return authenticatedUser;
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async userData => {
    try {
      const { data } = await api.post(
        "/auth/login",
        userData
      );

      return await initializeAuthenticatedSession(
        data
      );
    } catch (err) {
      toastError(err);
      setLoading(false);
      return null;
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      console.warn(
        "Não foi possível encerrar a sessão no servidor:",
        err?.response?.status || err?.message
      );
    } finally {
      setIsAuth(false);
      setUser({});

      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      localStorage.removeItem("companyDueDate");

      sessionStorage.removeItem(
        "korax_after_onboarding"
      );

      api.defaults.headers.Authorization = undefined;

      setLoading(false);

      window.location.replace("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data)
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
    refreshCurrentUser,
    initializeAuthenticatedSession,
  };
};

export default useAuth;
